<template>
  <div class="flex justify-center">
    <div class="w-full lg:w-1/3 lg:pr-8 lg:border-r lg:border-truegray-300">
      <div class="flex items-center mb-2">
        <button
          v-if="config?.preferences?.length"
          class="rounded-full border-truegray-300 border py-1 px-2 cursor-default"
        >
          <h6 class="text-truegray-500">
            {{ engagementType.firstPreference }}
          </h6>
        </button>
        <div
          v-if="engagementType?.extraPreferenceLength > 0"
          :title="engagementType.extraPreferenceStrings"
          class="rounded-full cursor-help w-6 h-5 text-xs text-truegray-500 bg-truegray-100 flex items-center justify-center m-0 ml-2 relative transition-all"
        >
          + {{ engagementType.extraPreferenceLength }}
        </div>
      </div>
      <h2>{{ config.report_name }}</h2>
      <p class="text-truegray-500 font-semibold text-xs">Engagement date:</p>
      <p class="mb-8 text-truegray-500 font-semibold text-xs">
        {{ config.engagement_date }}
      </p>
      <div v-if="config?.staff_member" class="mb-2">
        <p class="font-bold text-xs">Staff Member</p>
        <p class="text-truegray-500">{{ config.staff_member }}</p>
      </div>
      <div v-if="config?.staff_email" class="mb-8">
        <p class="font-bold text-xs">Staff Email</p>
        <p class="text-truegray-500">{{ config.staff_email }}</p>
      </div>
      <button
        v-if="config?.connectionContext"
        class="btn btn-black h-10 mb-4"
        @click="showConnectionContext"
      >
        Back
      </button>
    </div>
    <div class="w-full lg:w-2/3 lg:pl-8">
      <div class="space-y-4 mb-8">
        <div v-if="config.subjects?.length">
          <h5 class="mb-2 text-xs font-bold">
            Subject<span v-if="config.subjects.length > 1">s</span>
          </h5>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="subject in config.subjects"
              :key="subject.id"
              :class="subject.id"
              class="rounded-full px-2 py-1 bg-primary text-white text-xs"
            >
              {{ getSubjectTitle(subject) }}
            </div>
          </div>
        </div>

        <div v-if="config?.soft_skills?.length">
          <h5 class="mb-2 text-xs font-bold">
            Soft Skill<span v-if="config?.soft_skills?.length > 1">s</span>
          </h5>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="softSkill in config?.soft_skills"
              :key="softSkill.id"
              :class="softSkill.id"
              class="rounded-full px-2 py-1 border border-truegray-500 text-truegray-500 text-xs"
            >
              {{ softSkill.title }}
            </div>
          </div>
        </div>

        <div v-if="config?.tech_skills?.length">
          <h5 class="mb-2 text-xs font-bold">
            Technical Skill<span v-if="config.tech_skills.length > 1">s</span>
          </h5>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="techSkill in config.tech_skills"
              :key="techSkill.id"
              :class="techSkill.id"
              class="rounded-full px-2 py-1 border border-truegray-500 text-truegray-500 text-xs"
            >
              {{ techSkill.title }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="config?.contacts?.length" class="my-8">
        <h5 class="mb-2 text-xs font-bold">
          Connection<span v-if="config?.contacts?.length > 1">s</span>
        </h5>
        <div class="flex flex-wrap gap-4">
          <StackedConnections
            v-if="config?.contacts?.length > 1"
            :connections="config.contacts"
            :employment-details="true"
            :show-contact-details="true"
          />
          <div v-else>
            <ListConnection
              v-for="connection in config.contacts"
              :key="connection.id"
              :connection="connection"
            />
          </div>
        </div>
      </div>
      <div v-if="config?.teachers_note" class="mb-4">
        <h5 class="mb-2 text-xs font-bold">Teacher's note</h5>
        <p class="text-truegray-500">{{ config.teachers_note }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
// import { useStore } from '../../../../../js/store/index.js'
import ListConnection from '../ListConnection.vue'
import StackedConnections from '../StackedConnections.vue'
import { getTagDisplayTitle } from '../../../../../js/util/helper.js'
import { EventBus } from '../../../../../js/plugins/event-bus'

export default defineComponent({
  name: 'ModalReport',
  components: { ListConnection, StackedConnections },

  props: {
    config: { type: Object, required: true },
  },

  setup(props) {
    const engagementType = computed(() => {
      if (!props.config.preferences) {
        return null
      }
      let firstPreference = getTagDisplayTitle(
        props.config.preferences[0] || {},
        props.config.additional_info,
      )
      const extraPreferences = props.config.preferences
        .slice(1)
        .map((pref) => getTagDisplayTitle(pref, props.config.additional_info))
      const extraPreferenceLength = Math.max(extraPreferences.length, 0)
      const extraPreferenceStrings = extraPreferences.join(', ')

      return {
        firstPreference,
        extraPreferenceLength,
        extraPreferenceStrings,
      }
    })

    const getSubjectTitle = (subject) =>
      getTagDisplayTitle(subject, props.config.additional_info)

    const showConnectionContext = () => {
      EventBus.$emit('showConnectionContext', null)
    }

    return { engagementType, getSubjectTitle, showConnectionContext }
  },
})
</script>
